import { memo, useCallback } from 'react';

import { Button } from '@air/primitive-button';

import { DragType } from '~/components/Shared/Drag/dragTypes';
import { useScrollElementContext } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import {
  WorkspaceSidebarBoardsList,
  type WorkspaceSidebarBoardsListProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { WorkspaceSidebarBoardsListItemSkeleton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemSkeleton';
import { WorkspaceSidebarCollapsible } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCollapsible';
import { WorkspaceSidebarSection } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSection';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { WorkspaceSidebarWorkspaceBoardsHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarWorkspaceBoardsHeader';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useWorkspaceBoards } from '~/swr-hooks/boards/useWorkspaceBoards';
import { useRearrangeBoards } from '~/swr-hooks/gallery/useGalleryRearrange';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';
import {
  canChangeBoardAssetsOrBoardsPosition,
  canChangeWorkspaceAssetsOrBoardsPosition,
} from '~/utils/permissions/boardPermissions';

export const WorkspaceSidebarWorkspaceBoards = memo(() => {
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const { scrollElement } = useScrollElementContext();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { currentWorkspace } = useCurrentWorkspace();

  const {
    data: workspaceBoards,
    isLoadingMore,
    hasMore,
    isInitialLoading,
    loadNextPage,
  } = useWorkspaceBoards({
    includeDirectDescendantBoardCount: true,
    view: 'root',
    workspaceId: currentWorkspace?.id,
  });

  useFetchObjectsPermissions({
    objects: {
      boardIds: workspaceBoards.map((board) => board.id),
    },
  });

  const { getBoardPermissions } = useBoardPermissionsCache();
  const { rearrangeBoards } = useRearrangeBoards();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  const onBoardRearrange: WorkspaceSidebarBoardsListProps['onBoardRearrange'] = useCallback(
    ({ adjacentItem, boards, parentBoardId, board }) =>
      rearrangeBoards({
        boards,
        draggedItem: board,
        adjacentItem,
        parentBoardId,
      }),
    [rearrangeBoards],
  );

  const handleLoadNextPage = useCallback(() => {
    loadNextPage();
  }, [loadNextPage]);

  const getCanRearrange: WorkspaceSidebarBoardsListProps['getCanRearrange'] = useCallback(
    ({ parentBoardId }) => {
      const isSubnavCustomSort = getSubNavSortValue()?.boardSort?.name === 'customSort';

      /**
       * If the subnav has been sorted and it's value isn't "Custom Sort",
       * don't allow them to rearrange the board since the order has been already set
       */
      if (!isSubnavCustomSort) {
        return false;
      }

      return parentBoardId
        ? canChangeBoardAssetsOrBoardsPosition(getBoardPermissions(parentBoardId))
        : canChangeWorkspaceAssetsOrBoardsPosition(permissions);
    },
    [getBoardPermissions, getSubNavSortValue, permissions],
  );

  return (
    <WorkspaceSidebarSection className="contents">
      <WorkspaceSidebarWorkspaceBoardsHeader />

      <WorkspaceSidebarCollapsible open={!collapsed}>
        {isInitialLoading ? (
          <>
            <WorkspaceSidebarBoardsListItemSkeleton />
            <WorkspaceSidebarBoardsListItemSkeleton />
          </>
        ) : (
          workspaceBoards?.length > 0 && (
            <WorkspaceSidebarBoardsList
              boards={workspaceBoards}
              depth={1}
              dragType={DragType.boardNavWorkspace}
              draggable={true}
              droppable={true}
              getCanRearrange={getCanRearrange}
              onBoardRearrange={onBoardRearrange}
              parentBoardId={null}
              scrollElement={scrollElement}
            />
          )
        )}
        {hasMore ? (
          <Button
            appearance="ghost"
            className="ml-9 justify-start"
            color="gray"
            data-testid="WORKSPACE_SIDEBAR_WORKSPACE_BOARDS_SHOW_MORE"
            isLoading={isLoadingMore}
            onClick={handleLoadNextPage}
            size="small"
          >
            Show more
          </Button>
        ) : null}
      </WorkspaceSidebarCollapsible>
    </WorkspaceSidebarSection>
  );
});

WorkspaceSidebarWorkspaceBoards.displayName = 'WorkspaceSidebarWorkspaceBoards';
